import { useMemo, useState } from 'react';
import { Autocomplete, Box, CircularProgress, FormHelperText, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAirportsByCityId, getAllAirports, searchAirports } from '../../../api/endpoints/air';
import type { AirportListItem, AirportOption } from '../../../api/types/air';

/** `Airport/Get` full-dump row → the camelCase `AirportOption` shape `Airport/GetWithQuery` returns. */
function toOption(a: AirportListItem): AirportOption {
  return {
    code: a.code,
    display: a.label,
    slug: a.slug,
    countryCode: a.country_code,
    countryName: a.country_name,
    cityCode: a.city_code,
    cityName: a.city_name,
    isCity: a.is_city ? 'True' : 'False',
    keywords: a.keywords,
  };
}

/** Origin/destination autocomplete backed by `Airport/GetWithQuery` (min 3 chars, debounced
 *  via staleTime). Falls back to a client-side filter over the full `Airport/Get` list when
 *  the query endpoint errors; when a city (not a specific airport) is picked, its member
 *  airports are listed below the field via `Airport/GetAirportsAirportByCityID`. */
export function AirportAutocomplete({
  label,
  value,
  onChange,
}: {
  label: string;
  value: AirportOption | null;
  onChange: (value: AirportOption | null) => void;
}) {
  const [inputValue, setInputValue] = useState('');
  const trimmed = inputValue.trim();

  const { data: queryOptions = [], isFetching, isError } = useQuery({
    queryKey: ['air', 'airportQuery', trimmed],
    queryFn: () => searchAirports(trimmed),
    enabled: trimmed.length >= 3,
    staleTime: 60_000,
  });

  // Fallback full list — only fetched once the query endpoint has errored.
  const { data: fullList = [] } = useQuery({
    queryKey: ['air', 'airportsFull'],
    queryFn: () => getAllAirports(),
    enabled: isError,
    staleTime: 60 * 60_000,
  });

  const options = useMemo(() => {
    if (!isError || trimmed.length < 3) return queryOptions;
    const lower = trimmed.toLocaleLowerCase('tr');
    return fullList
      .filter(
        (a: AirportListItem) =>
          (a.code ?? '').toLocaleLowerCase('tr').includes(lower) ||
          (a.label ?? '').toLocaleLowerCase('tr').includes(lower) ||
          (a.keywords ?? '').toLocaleLowerCase('tr').includes(lower)
      )
      .slice(0, 50)
      .map(toOption);
  }, [isError, trimmed, queryOptions, fullList]);

  // Member airports of a picked city.
  const { data: cityAirports = [] } = useQuery({
    queryKey: ['air', 'cityAirports', value?.code],
    queryFn: () => getAirportsByCityId(value!.code as string),
    enabled: !!value?.code && value?.isCity === 'True',
    staleTime: 60 * 60_000,
  });

  return (
    <Box sx={{ minWidth: 240, flex: 1 }}>
      <Autocomplete
        sx={{ width: '100%' }}
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        inputValue={inputValue}
        onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(option) => option.display ?? option.code ?? ''}
        isOptionEqualToValue={(option, val) => option.code === val.code}
        loading={isFetching}
        noOptionsText={trimmed.length < 3 ? 'Aramak için en az 3 karakter girin' : 'Havalimanı bulunamadı'}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {isFetching && <CircularProgress color="inherit" size={16} />}
                    {params.slotProps.input.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />
      {cityAirports.length > 0 && (
        <FormHelperText>
          Şehirdeki havalimanları: {cityAirports.map((a: AirportListItem) => a.code).filter(Boolean).join(', ')}
        </FormHelperText>
      )}
    </Box>
  );
}
